// A photo reorder or "Set as main" saved offline (BL-2418, ADR 066 — Offline Mode § 4.6): the device's
// copy of the lot in the new order, folded by the one order rule the server runs (`applyLotMediaOrder`).
// The order itself is part of the lot's desired state and goes out with it (BL-2815).
import type { InventoryMediaItem } from '@auctogon/core/inventory';
import type { OfflineDeltaLotDto } from '@auctogon/core-admin/schemas/responses/offline-sync';

import { applyLotMediaOrder } from '../lots/lot-attachment-edit';

/**
 * The list row's photos in the new order: its cover follows Main (operator Q3, 2026-10-12). The
 * Save's adds are already on the row (BUG-2081), so a photo picked offline and made Main is the
 * row's cover too.
 */
function rowMediaInOrder(row: readonly InventoryMediaItem[], order: readonly string[]): InventoryMediaItem[] {
  const ordered = applyLotMediaOrder(row, order);
  return 'next' in ordered ? ordered.next : [...row];
}

/**
 * The device's copy of a lot after a Save's photo order: the detail's photos in that order with the
 * first as Main, and the list row following it.
 *
 * @param input.lot - the cached lot, after the Save's photo adds and removes.
 * @param input.order - the photo ids in their new order.
 * @returns the lot; unchanged when the order is already in place.
 */
export function applyOfflineMediaOrder(input: { lot: OfflineDeltaLotDto; order: readonly string[] }): OfflineDeltaLotDto {
  const { lot, order } = input;
  const ordered = applyLotMediaOrder(lot.detail.media, order);
  if (!('next' in ordered)) return lot;
  const media = ordered.next;
  return {
    ...lot,
    detail: { ...lot.detail, media },
    row: lot.row.media ? { ...lot.row, media: rowMediaInOrder(lot.row.media, order) } : lot.row,
  };
}
